import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { makeStyles, Theme } from '@material-ui/core/styles';
import AppHeaderOptionsMenu from 'components/@layout/AppBar/AppHeaderOptionsMenu';
import REMOVE_CLASSROOMS_MUTATION from 'graph/classroom/remove-classrooms-mutation';
import ARCHIVE_CLASSROOMS_MUTATION from 'graph/classroom/archive-classrooms-mutation';
import useRouteAction from 'hooks/route-action/use-route-action';
import useStore from 'store';
import { SweetAlertOptions } from 'sweetalert2';
import { ModuleProps } from 'types';
import { AlertVariant } from 'types/enums';
import AddStudentModal from '../../Classrooms/modals/AddStudentsModal';
import RemoveClassroomModal from '../../Classrooms/modals/RemoveClassroomModal';
import { ClassroomModalVariant, ClassroomModalRouteAction, RemoveVariant } from '../../types';
import { getClassroomsMutationCacheUpdate } from '../../util/update-cache';

export interface MoreOptionsHandlers {
  handleEditClass?: React.ReactEventHandler<HTMLLIElement>;
  onArchiveMultipleClick: () => void;
  onDeleteMultipleClick: () => void;
}

type ClassroomOptionsMenuProps = ModuleProps & {
  setLoading: (value: boolean) => void;
  admin?: boolean;
};

const useStyles = makeStyles((theme: Theme) => {
  return {
    moreOptionsIcon: {
      color: 'black'
    },
    modalContentBlock: {
      margin: theme.spacing(4),
      height: 400,
      background: theme.palette.divider,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
  };
});

/** TODO: all of these modal builders should be merged into some sort of factory */
const modalViewRouteActionMap: { [key: string]: string } = {
  [ClassroomModalVariant.EDIT]: ClassroomModalRouteAction.EDIT_CLASSROOM,
  [ClassroomModalVariant.ARCHIVE]: ClassroomModalRouteAction.ARCHIVE_CLASSROOMS,
  [ClassroomModalVariant.DELETE]: ClassroomModalRouteAction.DELETE_CLASSROOMS
};

const getModalTitle = (view: ClassroomModalVariant): string => {
  switch (view) {
    case ClassroomModalVariant.ARCHIVE: {
      return 'Archive Classes';
    }
    case ClassroomModalVariant.DELETE: {
      return 'Delete Classes';
    }
    default:
      return '';
  }
};

const getMoreOptions = ({
  onArchiveMultipleClick,
  onDeleteMultipleClick
}: MoreOptionsHandlers): any => {
  return [
    {
      label: 'class-actions',
      options: [
        /* TODO: Future Implementation
        {
          label: 'Manage Class Settings'
        },
        {
          label: 'Print Student Login Cards'
        },
        */
        {
          label: 'Archive Classes',
          onClick: onArchiveMultipleClick
        },
        {
          label: 'Delete Classes',
          onClick: onDeleteMultipleClick
        }
      ]
    }
  ];
};

interface ModalHandlers {
  closeModal: () => void;
  showArchiveAlert: (
    params: { overrides: SweetAlertOptions },
    selectedClassroomIds: number[]
  ) => void;
  showDeleteAlert: (
    params: { overrides: SweetAlertOptions },
    selectedClassroomIds: number[]
  ) => void;
}

const getModal = (
  classroomModalMode: ClassroomModalVariant,
  admin = false,
  { closeModal: handleOnClose, showArchiveAlert, showDeleteAlert }: ModalHandlers
): JSX.Element => {
  switch (classroomModalMode) {
    case ClassroomModalVariant.ARCHIVE: {
      return (
        <RemoveClassroomModal admin={admin} onConfirm={showArchiveAlert} onClose={handleOnClose} />
      );
    }
    case ClassroomModalVariant.DELETE: {
      return (
        <RemoveClassroomModal
          admin={admin}
          variant={RemoveVariant.DELETE}
          onConfirm={showDeleteAlert}
          onClose={handleOnClose}
        />
      );
    }
    case ClassroomModalVariant.ADD_STUDENT: {
      return <AddStudentModal onClose={handleOnClose} />;
    }
    default:
      throw new Error(`No modal case for ${classroomModalMode}`);
  }
};

const ClassroomsOptionsMenu: React.FC<ClassroomOptionsMenuProps> = ({ id, admin, setLoading }) => {
  const { t } = useTranslation(id);
  const classes = useStyles();

  const [{ user }, { openFullModal, closeModal, openAlert }] = useStore();
  const [removeClassrooms] = useMutation(
    REMOVE_CLASSROOMS_MUTATION,
    getClassroomsMutationCacheUpdate(Number(user.me?.id), admin)
  );
  const [archiveClassrooms] = useMutation(
    ARCHIVE_CLASSROOMS_MUTATION,
    getClassroomsMutationCacheUpdate(Number(user.me?.id), admin)
  );

  // TODO: Handle errors
  const deleteClassroomsCallback = React.useCallback(
    (title: string, text: string, classroomIds: any) => async (): Promise<void> => {
      try {
        setLoading(true);
        await removeClassrooms({
          variables: {
            where: {
              id: classroomIds
            }
          }
        });
        openAlert({
          variant: AlertVariant.SUCCESS,
          title,
          text
        });
      } catch (error) {
        return;
      }
      setLoading(false);
    },
    [openAlert, removeClassrooms, setLoading]
  );

  // TODO: Handle errors
  const archiveClassroomsCallback = React.useCallback(
    (title: string, text: string, classroomIds: number[]) => async (): Promise<void> => {
      try {
        setLoading(true);
        await archiveClassrooms({
          variables: {
            where: {
              id: classroomIds
            }
          }
        });
        openAlert({
          variant: AlertVariant.SUCCESS,
          title,
          text
        });
      } catch (error) {
        return;
      }
      setLoading(false);
    },
    [openAlert, archiveClassrooms, setLoading]
  );

  const showArchiveAlert = React.useCallback(
    ({ overrides }: { overrides?: SweetAlertOptions }, selectedClassroomIds: number[]): void => {
      openAlert({
        variant: AlertVariant.CLASSROOM_ARCHIVE,
        html: overrides?.html || {},
        textItems: selectedClassroomIds,
        onSubmit: archiveClassroomsCallback(
          t('successTitle', { count: selectedClassroomIds.length, ns: 'ArchiveClassroomsAlert' }),
          t('successMessage', { count: selectedClassroomIds.length, ns: 'ArchiveClassroomsAlert' }),
          selectedClassroomIds
        )
      });
    },
    [openAlert, archiveClassroomsCallback, t]
  );

  const showDeleteAlert = React.useCallback(
    ({ overrides }: { overrides?: SweetAlertOptions }, selectedClassroomIds: number[]): void => {
      const deleteText = t('deleteConfirmInstructions', { ns: '@common' });
      const title = t('title', { count: selectedClassroomIds.length, ns: 'DeleteClassroomsAlert' });
      const confirmButtonText = t('confirm', {
        count: selectedClassroomIds.length,
        ns: 'DeleteClassroomsAlert'
      });

      openAlert({
        variant: AlertVariant.DELETE,
        title,
        confirmButtonText,
        deleteText,
        html: overrides?.html,
        onSubmit: deleteClassroomsCallback(
          t('successTitle', { count: selectedClassroomIds.length, ns: 'DeleteClassroomsAlert' }),
          t('successMessage', { count: selectedClassroomIds.length, ns: 'DeleteClassroomsAlert' }),
          selectedClassroomIds
        )
      });
    },
    [openAlert, deleteClassroomsCallback, t]
  );

  const handleClassroomModalModification = (
    classroomModalView: ClassroomModalVariant
  ): (() => void) => {
    return (): void => {
      openFullModal({
        title: getModalTitle(classroomModalView),
        body: getModal(classroomModalView, admin, {
          closeModal,
          showArchiveAlert,
          showDeleteAlert
        }),
        routeAction: modalViewRouteActionMap[classroomModalView],
        routeParams: {}
      });
    };
  };

  const options = getMoreOptions({
    handleEditClass: handleClassroomModalModification(ClassroomModalVariant.EDIT),
    onArchiveMultipleClick: handleClassroomModalModification(ClassroomModalVariant.ARCHIVE),
    onDeleteMultipleClick: handleClassroomModalModification(ClassroomModalVariant.DELETE)
  });

  useRouteAction(({ action }) => {
    if (!action) return;
    switch (action) {
      case ClassroomModalRouteAction.EDIT_CLASSROOM:
        return handleClassroomModalModification(ClassroomModalVariant.EDIT)();
      case ClassroomModalRouteAction.ARCHIVE_CLASSROOMS:
        return handleClassroomModalModification(ClassroomModalVariant.ARCHIVE)();
      case ClassroomModalRouteAction.DELETE_CLASSROOMS:
        return handleClassroomModalModification(ClassroomModalVariant.DELETE)();
      default:
        return null;
    }
  });

  return (
    <React.Fragment>
      <AppHeaderOptionsMenu
        buttonSize="small"
        id={'more-options-menu'}
        aria-label="More options menu."
        iconClassName={classes.moreOptionsIcon}
        optionSections={options}
      />
    </React.Fragment>
  );
};

export default ClassroomsOptionsMenu;
